import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AppearanceProvider, ThemeProvider } from "@eduviaos/ui-kit";
import { getCurrentTenant } from "@eduviaos/shared";
import { AppFrame } from "@/components/AppFrame";
import { TenantBrandingProvider } from "@/components/TenantBrandingProvider";
import { ApplyPage } from "@/pages/ApplyPage";
import { HomePage } from "@/pages/HomePage";
import { IntakePage } from "@/pages/IntakePage";
import { TrackPage } from "@/pages/TrackPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

/**
 * Public-surface QueryClient.
 *
 * `staleTime: 0` and `refetchOnMount: "always"` because every public
 * payload either:
 *   - changes server-side without notice (form-definition edits,
 *     funnel transitions made by IA staff), or
 *   - has Cache-Control: no-store applied by the BE.
 *
 * Aggressive freshness avoids the parent seeing a stale form schema
 * after the institution has updated their intake fields.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 0,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  // Phase H0 hotfix: read the tenant slug from the host once at app
  // boot so <TenantBrandingProvider> can apply the institution's
  // palette + favicon to the apply portal. Falsy on the bare apex
  // (dev), where the provider is a no-op and the default palette
  // renders.
  const tenantSlug = getCurrentTenant().slug ?? null;
  return (
    // ThemeProvider drives the legacy `useTheme` hook used by
    // `<ThemeSwitcher>`; AppearanceProvider drives the broader
    // appearance axes (palette, radius, scale). Both write to the
    // `<html data-theme>` attribute and stay in sync via the shared
    // `eos:theme` localStorage key.
    <ThemeProvider defaultTheme="system">
      <AppearanceProvider defaultPalette="ocean-breeze">
        <QueryClientProvider client={queryClient}>
          <TenantBrandingProvider slug={tenantSlug}>
            <BrowserRouter>
              {/*
              Routes (D2d):
                /             — intake form when on a tenant subdomain
                                (<slug>--apply-{stage,pr-N}.eduviaos.com)
                                or HomePage explainer when no tenant
                                signal is present (bare apex / dev).
                /track/:token — tracker for the enquiry; tenant comes
                                from the host, never from the URL path.
                /not-found    — explicit landing for legacy / malformed
                                URLs to avoid router crashes.

              The /intake/:tenantSlug + /track/:tenantSlug/:token routes
              from D2b are gone — the slug now lives on the host name
              (matches IA's <slug>--ia-stage.eduviaos.com pattern).
              Legacy URLs from any cached share-card bounce to /not-found
              with a helpful message rather than a 500.
            */}
              <Routes>
                <Route element={<AppFrame />}>
                  <Route path="/" element={<IntakePage />} />
                  <Route path="/track/:token" element={<TrackPage />} />
                  {/* Phase F4 — apply portal entry. The token's purpose
                    (apply_form / attempt_assessment / view_status)
                    is decided server-side; the page dispatches to the
                    matching section component on a single URL. */}
                  <Route path="/apply/:token" element={<ApplyPage />} />
                  {/* Legacy compat — /intake/<slug> and /track/<slug>/<token>
                    URLs that may still be in the wild (parent emails sent
                    in the ~hour D2b was live). Bounce to NotFound rather
                    than a router crash. We DON'T redirect to /track/<token>
                    because that would silently change the wrong tenant's
                    URL. */}
                  <Route path="/intake/*" element={<Navigate to="/not-found" replace />} />
                  <Route path="/track/*" element={<Navigate to="/not-found" replace />} />
                  <Route path="/apply/*" element={<Navigate to="/not-found" replace />} />
                  <Route path="/welcome" element={<HomePage />} />
                  <Route path="/not-found" element={<NotFoundPage />} />
                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </BrowserRouter>
          </TenantBrandingProvider>
        </QueryClientProvider>
      </AppearanceProvider>
    </ThemeProvider>
  );
}
